import {
  Checkbox,
  DefaultButton,
  mergeStyleSets,
  TooltipHost,
} from "@fluentui/react";
import ComboBoxField from "components/controls/ComboBoxField";
import DatePickerField from "components/controls/DatePickerField";
import DoubleNumberInputField from "components/controls/DoubleNumberInputField";
import GroupPolicyNumberField from "components/controls/GroupPolicyNumberField";
import NumberInputField from "components/controls/NumberInputField";
import TextInputField from "components/controls/TextInputField";
import { toast } from "components/FluentToast";
import { FormSpinner } from "components/FormSpinner";
import { StyledPrimaryButton } from "components/styled";
import { Formik } from "formik";
import { useAdjustDeductibleFleetPremium } from "hooks/data/mutations/useAdjustDeductibleFleetPremium";
import { delay } from "library/delay";
import moment from "moment";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { createFieldPairValidation } from "store/util";
import { setYupLocale } from "utils/setYupLocale";
import { formatDoubleFieldValue } from "utils/utils";
import * as yup from "yup";
import {
  setDialog,
  setDialogDefaultValueOperation,
  setFleetWithPremiums,
} from "../../../store/fleetForm";
import { useSelector } from "../../../store/hooks";
import useActionConfig from "../hooks/useActionConfg";
import FormActionInsurerField from "./components/FormActionInsurerField";
import { useDefaultSettings } from "./hooks/useDefaultSettings";
import VehicleTypeDependencyField from "./VehicleTypeDependencyField";

const AdjustDeductibleFormDialog = () => {
  const { t } = useTranslation();
  const [isTradeLicensePlate, setTradeLicensePlate] = useState(false);
  const { mutate: onAdjustDeductible, isLoading } =
    useAdjustDeductibleFleetPremium();

  const taxonomy = useSelector((s) => s.taxonomy);

  const fleetAppSettings = useSelector((s) => s.fleetForm.fleetAppSettings);
  const hullGroupPolicyInfo = useSelector(
    (s) => s.fleetForm.fleetWithPremiums.hullGroupPolicyInfo
  );
  const actionsConfig = useActionConfig(fleetAppSettings);
  const dialogData = useSelector((s) => s.fleetForm.dialog);
  const defaultData =
    dialogData.type === "editSetting" && dialogData.data?.selectedSetting;
  const carUsageOptions = actionsConfig.carUsageOptions;

  const dispatch = useDispatch();
  const { fleetId } = useParams() as any;

  useDefaultSettings();

  const close = () => {
    dispatch(setDialog({ type: "", isOpen: false }));
  };

  const vehicleTypeCodeOptions = actionsConfig.vehicleTypeCodeOptions;
  const listPriceTypeOptions = actionsConfig.listPriceTypeOptions;
  const insurerOptions = actionsConfig.insurerOptions;
  const vehiclePolicyStatusOptions = actionsConfig.vehiclePolicyStatusOptions;

  const initialValues = {
    hullDeductiblePercent: null,
    hullDeductibleMinAmount: null,
    hullDeductibleMaxAmount: null,
    hullDeductibleComment: null,
    insurerGroupPolicyNumber: null,
    policyStatus: null,
    insurerPartnerNumber: null,
    vehicleType: null,
    listPriceType: null,
    listPriceMin: null,
    listPriceMax: null,

    cubicCapacityCcmRange: null,
    weightCapacityKgRange: null,
    truckMounting: null,
    validFromDate: new Date(new Date().getFullYear(), 0, 1),
    reasonForModificationComment: null,
    carUsage: null,

    emptyWeightKgFrom: null,
    emptyWeightKgTo: null,
    powerKwFrom: null,
    powerKwTo: null,
  };

  const isAustria = fleetAppSettings?.COUNTRY_CODE === "Country.AT";

  const validationSchema = useMemo(() => {
    setYupLocale(t);
    return yup
      .object()
      .shape({
        hullDeductiblePercent: isAustria
          ? yup.number().required().max(100)
          : yup.number().nullable().max(100),
        hullDeductibleMinAmount: isAustria
          ? yup.string().required()
          : yup.string().nullable(),
        hullDeductibleMaxAmount: isAustria
          ? yup.string().when("hullDeductiblePercent", {
              is: (val) => !val || Number(val) <= 0,
              then: yup.string().required(),
              otherwise: yup.string().nullable(),
            })
          : yup.string().nullable(),
        hullDeductibleComment: isAustria
          ? yup.string().required()
          : yup.string().nullable(),
        insurerPartnerNumber: yup.mixed().required(),
        cubicCapacityCcmRange: yup.mixed().nullable(),
        weightCapacityKgRange: yup.mixed().nullable(),
        truckMounting: yup.mixed().nullable(),
        validFromDate: yup.mixed().required(),
        vehicleType: isTradeLicensePlate
          ? yup.mixed().nullable()
          : yup.mixed().required(),
        reasonForModificationComment: yup.string().max(512).nullable(),

        emptyWeightKgFrom: yup.number().nullable(),
        emptyWeightKgTo: yup.number().nullable(),
        powerKwFrom: yup.number().nullable(),
        powerKwTo: yup.number().nullable(),
      })
      .test(
        "emptyWeightKg",
        createFieldPairValidation("emptyWeightKgFrom", "emptyWeightKgTo")
      )
      .test("powerKw", createFieldPairValidation("powerKwFrom", "powerKwTo"))
      .test(
        "listPrice",
        createFieldPairValidation("listPriceMin", "listPriceMax", true)
      );
  }, [t, isTradeLicensePlate, isAustria]);
  const classes = getClassNames();

  const onSubmit = (values) => {
    let body: any = {
      criteria: {
        vehicleTypeCodes: isTradeLicensePlate
          ? null
          : values?.vehicleType.map((item) => item.value),

        carUsageCodes: values?.carUsage
          ? values?.carUsage.map((item) => item.value)
          : null,
        truckMountingCodes: values?.truckMounting
          ? values?.truckMounting?.map((item) => item.value)
          : null,
        weightCapacityKgRangeCodes: values?.weightCapacityKgRange
          ? values?.weightCapacityKgRange?.map((item) => item.value)
          : null,
        maxGrossWeightKgRangeCodes: values?.maxGrossWeightKgRange
          ? values?.maxGrossWeightKgRange?.map((item) => item.value)
          : null,
        powerKws:
          values?.powerKwFrom && values?.powerKwTo
            ? [
                {
                  from: values?.powerKwFrom,
                  to: values?.powerKwTo,
                },
              ]
            : null,
        emptyWeightKgs:
          values?.emptyWeightKgFrom && values?.emptyWeightKgTo
            ? [
                {
                  from: values?.emptyWeightKgFrom,
                  to: values?.emptyWeightKgTo,
                },
              ]
            : null,
        cubicCapacityCcmRangeCodes: values?.cubicCapacityCcmRange
          ? values?.cubicCapacityCcmRange?.map((item) => item.value)
          : null,
        numberOfSeatsRangeCodes: values?.numberOfSeatsRange
          ? values?.numberOfSeatsRange?.map((item) => item.value)
          : null,
        numberOfSeatsMopedCodes: values?.numberOfSeatsMoped
          ? values?.numberOfSeatsMoped?.map((item) => item.value)
          : null,
        listPrices: values?.listPriceType
          ? [
              {
                type: values?.listPriceType?.value
                  ? values?.listPriceType?.value
                  : null,
                min: values?.listPriceMin
                  ? Number(formatDoubleFieldValue(values?.listPriceMin))
                  : undefined,
                max: values?.listPriceMax
                  ? Number(formatDoubleFieldValue(values?.listPriceMax))
                  : undefined,
              },
            ]
          : null,
        isTradeLicensePlate: isTradeLicensePlate,
      },
      // isTradeLicensePlate: isTradeLicensePlate,
      deductiblePercent:
        values.hullDeductiblePercent !== null &&
        values.hullDeductiblePercent !== undefined &&
        values.hullDeductiblePercent !== ""
          ? parseInt(
              (values.hullDeductiblePercent as string)
                ?.toString()
                ?.replaceAll(".", "")
                ?.replaceAll(",", ".")
            )
          : null,
      deductibleMinAmount: values.hullDeductibleMinAmount
        ? Number(formatDoubleFieldValue(values.hullDeductibleMinAmount))
        : null,
      deductibleMaxAmount: values.hullDeductibleMaxAmount
        ? Number(formatDoubleFieldValue(values.hullDeductibleMaxAmount))
        : null,
      deductibleComment: values.hullDeductibleComment || null,
      // listPriceType: values?.listPriceType?.value,
      // listPriceMin: values?.listPriceMin
      //   ? Number(formatDoubleFieldValue(values?.listPriceMin))
      //   : undefined,
      // listPriceMax: values?.listPriceMax
      //   ? Number(formatDoubleFieldValue(values?.listPriceMax))
      //   : undefined,
      insurerPartnerNumber: values?.insurerPartnerNumber?.value,
      // vehicleType: isTradeLicensePlate ? null : values?.vehicleType?.value,
      // weightCapacityKgRange: values?.weightCapacityKgRange?.value,
      // maxGrossWeightKgRange: values?.maxGrossWeightKgRange?.value,
      // cubicCapacityCcmRange: values?.cubicCapacityCcmRange?.value,
      // powerKwFrom: values?.powerKwFrom,
      // powerKwTo: values?.powerKwTo,

      // numberOfSeatsRange: values?.numberOfSeatsRange?.value,
      // numberOfSeatsMoped: values?.numberOfSeatsMoped?.value,
      validFromDate: moment(values?.validFromDate).format("YYYY-MM-DD"),
      reasonForModificationComment: values?.reasonForModificationComment,
      // carUsage: values?.carUsage?.value,

      premiumType: 2, // HULL VALUE

      // emptyWeightKgFrom: values?.emptyWeightKgFrom,
      // emptyWeightKgTo: values?.emptyWeightKgTo,
      ...((fleetAppSettings?.DEFAULT_COLUMNS.includes("mtplPolicyStatus") ||
        fleetAppSettings?.DEFAULT_COLUMNS.includes("hullPolicyStatus")) && {
        policyStatus: values?.policyStatus?.value,
      }),
    };
    if (fleetAppSettings?.INCLUDE_GROUP_POLICY) {
      body = {
        ...body,
        insurerGroupPolicyNumber: values?.insurerGroupPolicyNumber
          ? values?.insurerGroupPolicyNumber.value
          : null,
      };
    }

    onAdjustDeductible(
      {
        fleetId,
        body,
      },
      {
        onSuccess: (res) => {
          dispatch(setFleetWithPremiums(res?.data));
          toast.success(t("bfm.success"));
          close();
        },
      }
    );
  };
  const validate = (values) => {
    const errors = {} as any;

    return errors;
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      validate={validate}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={async (values, { setSubmitting }) => {
        onSubmit(values);
        await delay(400);
        setSubmitting(false);
      }}
    >
      {(props) => {
        const { values, handleSubmit, setFieldValue, handleChange, errors } =
          props;
        const insurerGroupPolicyNumberOptions = () => {
          return hullGroupPolicyInfo
            ?.filter(
              (el) =>
                el.groupPolicyNumber !== null && el.groupPolicyNumber !== ""
            )
            .map((policyInfo) => ({
              label: policyInfo.groupPolicyNumber,
              value: policyInfo.groupPolicyNumber,
            }));
        };

        return (
          <>
            <div className={classes.main}>
              <form onSubmit={handleSubmit}>
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <div className={classes.fieldItem}>
                    <Checkbox
                      label={t("bfm.tradeLicensePlates.label")}
                      styles={{
                        root: {
                          fontWeight: 600,
                        },
                      }}
                      disabled={false}
                      checked={isTradeLicensePlate}
                      onChange={(ev) => {
                        setFieldValue("vehicleType", null);
                        setFieldValue("carUsage", null);
                        setTradeLicensePlate(!isTradeLicensePlate);
                      }}
                    />
                  </div>
                </div>
                <div className={classes.fieldItem}>
                  <NumberInputField
                    setFieldValue={setFieldValue}
                    value={values.hullDeductiblePercent}
                    onChange={handleChange}
                    errors={errors}
                    required={isAustria}
                    name={"hullDeductiblePercent"}
                    disabled={false}
                    label={"bfm.vehicleForm.hullDeductiblePercent.label"}
                  />
                </div>
                <div className={classes.fieldItem}>
                  <DoubleNumberInputField
                    setFieldValue={setFieldValue}
                    value={values.hullDeductibleMinAmount}
                    onChange={handleChange}
                    errors={errors}
                    required={isAustria}
                    name={"hullDeductibleMinAmount"}
                    disabled={false}
                    label={"bfm.vehicleForm.hullDeductibleMinAmount.label"}
                  />
                </div>
                <div className={classes.fieldItem}>
                  <DoubleNumberInputField
                    setFieldValue={setFieldValue}
                    value={values.hullDeductibleMaxAmount}
                    onChange={handleChange}
                    errors={errors}
                    required={
                      isAustria &&
                      (!values.hullDeductiblePercent ||
                        Number(values.hullDeductiblePercent) <= 0)
                    }
                    name={"hullDeductibleMaxAmount"}
                    disabled={false}
                    label={"bfm.vehicleForm.hullDeductibleMaxAmount.label"}
                  />
                </div>
                <div className={classes.fieldItem}>
                  <TextInputField
                    multiline
                    rows={2}
                    value={values.hullDeductibleComment}
                    onChange={handleChange}
                    errors={errors}
                    required={isAustria}
                    name={"hullDeductibleComment"}
                    disabled={false}
                    label={"bfm.vehicleForm.hullDeductibleComment.label"}
                  />
                </div>

                {fleetAppSettings?.DEFAULT_COLUMNS.includes(
                  "mtplPolicyStatus"
                ) ||
                fleetAppSettings?.DEFAULT_COLUMNS.includes(
                  "hullPolicyStatus"
                ) ? (
                  <div className={classes.fieldItem}>
                    <ComboBoxField
                      disabled={false}
                      errors={errors}
                      name="policyStatus"
                      label={"bfm.vehicleForm.policyStatus.label"}
                      setFieldValue={setFieldValue}
                      options={vehiclePolicyStatusOptions || []}
                      defaultValue={null}
                      confidentiality={null}
                    />
                  </div>
                ) : null}
                <div className={classes.fieldItem}>
                  <FormActionInsurerField
                    actionFormType={"adjustDeductible"}
                    allInsurerOptions={insurerOptions}
                    values={values}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    onChange={(selectedOption) => {
                      // When insurer changes, clear the group policy number
                      setFieldValue("insurerPartnerNumber", selectedOption);
                      setFieldValue("insurerGroupPolicyNumber", null);
                    }}
                  />
                </div>
                {fleetAppSettings?.INCLUDE_GROUP_POLICY && (
                  <div className={classes.fieldItem}>
                    <GroupPolicyNumberField
                      id="insurerGroupPolicyNumber"
                      setFieldValue={setFieldValue}
                      options={insurerGroupPolicyNumberOptions() || []}
                      defaultValue={values?.insurerGroupPolicyNumber}
                      errors={errors}
                      name={"insurerGroupPolicyNumber"}
                      disabled={false}
                      label={"bfm.fleet.setting.insurerGroupPolicyNumber.label"}
                      confidentiality={null}
                      isClearable={false}
                    />
                  </div>
                )}
                <div className={classes.fieldItem}>
                  <ComboBoxField
                    disabled={false}
                    errors={errors}
                    name="listPriceType"
                    label={"bfm.vehicleForm.listPriceTypeCode.label"}
                    setFieldValue={setFieldValue}
                    options={listPriceTypeOptions || []}
                    defaultValue={values?.listPriceType}
                    confidentiality={null}
                  />
                </div>
                <div
                  className={classes.fieldItem}
                  style={{ display: "flex", gap: "15px" }}
                >
                  <div style={{ width: "50%" }}>
                    <DoubleNumberInputField
                      setFieldValue={setFieldValue}
                      value={values.listPriceMin}
                      onChange={handleChange}
                      errors={errors}
                      name={"listPriceMin"}
                      disabled={false}
                      label={"bfm.listPriceMin.label"}
                    />
                  </div>
                  <div style={{ width: "50%" }}>
                    <DoubleNumberInputField
                      setFieldValue={setFieldValue}
                      value={values.listPriceMax}
                      onChange={handleChange}
                      errors={errors}
                      name={"listPriceMax"}
                      disabled={false}
                      label={"bfm.listPriceMax.label"}
                    />
                  </div>
                </div>
                <div className={classes.fieldItem}>
                  {!isTradeLicensePlate ? (
                    <ComboBoxField
                      disabled={false}
                      errors={errors}
                      name="carUsage"
                      label={"bfm.vehicleForm.carUsageCode.label"}
                      setFieldValue={setFieldValue}
                      options={carUsageOptions || []}
                      defaultValue={values?.carUsage}
                      confidentiality={null}
                      isMulti={true}
                    />
                  ) : null}
                </div>
                {!isTradeLicensePlate ? (
                  <div className={classes.fieldItem}>
                    <ComboBoxField
                      disabled={isTradeLicensePlate}
                      errors={errors}
                      name="vehicleType"
                      required={true}
                      label={"bfm.vehicleForm.vehicleTypeCode.label"}
                      setFieldValue={setFieldValue}
                      options={vehicleTypeCodeOptions || []}
                      defaultValue={values?.vehicleType}
                      confidentiality={null}
                      isMulti={true}
                    />
                  </div>
                ) : null}
                <div className={classes.fieldItem}>
                  <VehicleTypeDependencyField
                    values={values}
                    vehicleTypeSelected={values?.vehicleType}
                    fieldItemStyle={classes.fieldItem}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    selectedDependency={defaultData}
                    disabled={false}
                    includeMargins={false}
                  />
                </div>
                <div className={classes.fieldItem}>
                  <DatePickerField
                    errors={errors}
                    setFieldValue={setFieldValue}
                    defaultValue={values.validFromDate}
                    name={"validFromDate"}
                    disabled={false}
                    label={"bfm.vehicleForm.validFromDate.label"}
                    placeholder={t("bfm.selectDate")}
                    required
                    confidentiality={null}
                  />
                </div>
                <div className={classes.fieldItem}>
                  <TextInputField
                    value={values.reasonForModificationComment}
                    onChange={handleChange}
                    errors={errors}
                    name={"reasonForModificationComment"}
                    multiline
                    rows={4}
                    disabled={false}
                    label={"bfm.comment.label"}
                  />
                </div>
                <div className={classes.footer}>
                  <TooltipHost content={t("bfm.fillInAllDefaults.label")}>
                    <StyledPrimaryButton
                      text={t("bfm.defaultSettings.label")}
                      onClick={() => {
                        let fieldNames1 = [];

                        //fieldNames1.push("annualGrossPremium");
                        fieldNames1.push("deductible");

                        dispatch(
                          setDialogDefaultValueOperation({
                            fieldNames: fieldNames1,
                            formValues: {
                              ...values,
                              premiumType: {
                                value:
                                  taxonomy.PremiumType.byCode[
                                    "PremiumType.HULL"
                                  ].id,
                              },
                              isTradeLicensePlate: isTradeLicensePlate,
                            },
                          })
                        );
                      }}
                      iconProps={{
                        iconName: "Shield",
                      }}
                      disabled={
                        isLoading ||
                        !values?.insurerPartnerNumber ||
                        !values["validFromDate"]
                      }
                    />
                  </TooltipHost>

                  <DefaultButton
                    primary
                    text={t("greco.save")}
                    type="submit"
                    iconProps={{
                      iconName: "Save",
                    }}
                    disabled={isLoading}
                  />
                </div>
                {isLoading && <FormSpinner />}
              </form>
            </div>
          </>
        );
      }}
    </Formik>
  );
};

const getClassNames = () =>
  mergeStyleSets({
    main: {},
    fieldItem: {
      width: "100%",
      marginTop: 20,
    },
    footer: {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
      marginTop: 150,
    },
  });

export default AdjustDeductibleFormDialog;
